import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import { CalendarMonth, OpenInNew } from "@mui/icons-material";
import Cookies from "js-cookie";
import axiosInstance from "../../../axios/axiosInstance";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3500";
const SCHEDULING_LINK = process.env.REACT_APP_SCHEDULING_LINK || "";

export default function ScheduleInterview() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [interview, setInterview] = useState({});
  const [interviewLoading, setInterviewLoading] = useState(true);

  const token = Cookies.get("token");

  const fetchInterviews = async () => {
    try {
      setInterviewLoading(true);
      const response = await axiosInstance.get(
        `/api/profile/candidate/candidate-eq-schedule`
      );
      setInterview(response.data);
    } catch (error) {
      console.error("Error fetching interview data:", error);
    } finally {
      setInterviewLoading(false);
    }
  };

  useEffect(() => {
    // Fetch user job roles
    fetch(`${API_BASE}/api/profile/user-job-roles`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setRoles(data))
      .catch((err) => console.error("Error fetching roles:", err));

    // Fetch user profile to get name and email
    fetch(`${API_BASE}/api/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setUserProfile(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching profile:", err);
        setLoading(false);
      });
    fetchInterviews();
  }, [token]);

  const handleScheduleClick = () => {
    if (SCHEDULING_LINK) {
      window.open(SCHEDULING_LINK, "_blank");
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Paper elevation={3} sx={{ p: 4, width: "100%", mx: "auto", mt: 0 }}>
      <Box sx={{ width: "100%", mx: "auto", mt: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Schedule an Interview
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Before joining the platform we need to confirm your expertise.
          Schedule an interview with the EQuAlly expert
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Roles you are requesting interview for
        </Typography>

        {/* 🔒 Non-selectable Roles */}
        <Box sx={{ mb: 3 }}>
          {roles.map((role) => (
            <Paper
              key={role.id}
              elevation={1}
              sx={{
                display: "inline-block",
                mr: 2,
                mb: 2,
                px: 3,
                py: 2,
                borderRadius: 2,
                minWidth: 220,
                background: "#f9f9f9",
                color: "text.secondary",
                cursor: "not-allowed",
                opacity: 0.7,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {role.job_role_name || role.role_name || role.name}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {role.job_function_name && (
                  <Chip
                    label={role.job_function_name}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {role.employment_type && (
                  <Chip
                    label={role.employment_type}
                    size="small"
                    variant="outlined"
                  />
                )}
                {role.location_type && (
                  <Chip
                    label={role.location_type}
                    size="small"
                    variant="outlined"
                  />
                )}
                {role.will_relocate &&
                  (role.will_relocate === true ||
                    role.will_relocate === "Yes") && (
                    <Chip
                      label="Willing to relocate"
                      size="small"
                      variant="outlined"
                    />
                  )}
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Show loader while fetching interview data */}
        {interviewLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading interview details...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Show scheduled interview details if already scheduled */}
            {interview?.hasSchedule && interview?.schedule ? (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Interview Scheduled Successfully!
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {" "}
                  For meeting link, please check the email you received when
                  scheduling your session.{" "}
                </Typography>
              </Alert>
            ) : (
              <>
                {/* Important Instructions */}
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Important Instructions:
                  </Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>
                        Use the <strong>same email and name</strong> for
                        scheduling that you used in this portal
                        {userProfile && (
                          <>
                            {" "}
                            (<strong>Name:</strong> {userProfile.first_name}{" "}
                            {userProfile.last_name}, <strong>Email:</strong>{" "}
                            {userProfile.email})
                          </>
                        )}
                      </li>
                      <li>This helps the interviewer easily identify you</li>
                      <li>
                        <strong>Select only ONE time slot</strong> from the
                        available options
                      </li>
                    </ul>
                  </Typography>
                </Alert>

                {/* Schedule Interview Button */}
                <Box sx={{ p: 3, mb: 3, textAlign: "center" }}>
                  <Typography variant="subtitle1" sx={{ mb: 3 }}>
                    Click the button below to schedule your interview
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<CalendarMonth />}
                    endIcon={<OpenInNew />}
                    onClick={handleScheduleClick}
                    disabled={!SCHEDULING_LINK}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: "1.1rem",
                      textTransform: "none",
                    }}
                  >
                    Schedule Interview
                  </Button>

                  {!SCHEDULING_LINK && (
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                      Scheduling link is not configured. Please contact support.
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
}
